import { useState } from "react";
import type { Locale } from "../shared/dto";

/**
 * The one language switch of the application.
 *
 * F116 gave every reader the language their browser asks for and stored the
 * choice on the account (`users.locale`, `PATCH /api/auth/locale`). The switch
 * itself, however, lived inline in `App.tsx` and was rendered on ONE surface -
 * the client's Account page. `Dashboard` returns the staff console BEFORE that
 * surface exists, so a partner or an admin could not reach it at all: David
 * (admin@buddy.fm) asked for an English console on 2026-08-19 and the only way
 * to give it to him was an UPDATE on the production database.
 *
 * The component moved out of `App.tsx` on purpose: `App` imports
 * `StaffConsole`, so the console cannot import back from `App` without a
 * cycle. Both surfaces now import it from here, and there is still exactly one
 * place that speaks to `/api/auth/locale`.
 *
 * The copy keys are props because the two surfaces say different true things:
 * the client's Account page promises "every client screen", the console
 * promises the console. Everything else - the two buttons, the request, the
 * saved and failed sentences - is shared and must stay shared.
 */
export function LanguagePreference(props: {
  locale: Locale;
  csrfToken: string;
  t: (key: string) => string;
  onChanged: (locale: Locale) => void;
  titleKey?: string;
  copyKey?: string;
}) {
  const [saving, setSaving] = useState(false);
  const [state, setState] = useState<"idle" | "saved" | "error">("idle");
  const titleKey = props.titleKey ?? "account.language.title";
  const copyKey = props.copyKey ?? "account.language.copy";

  async function changeLocale(locale: Locale) {
    if (locale === props.locale || saving) return;
    setSaving(true);
    setState("idle");
    try {
      const response = await fetch("/api/auth/locale", {
        method: "PATCH",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-Launchpad-CSRF": props.csrfToken
        },
        body: JSON.stringify({ locale })
      });
      const body = await response.json() as { ok?: boolean; locale?: Locale };
      if (!response.ok || body.ok !== true || body.locale !== locale) throw new Error("locale_update_failed");
      props.onChanged(locale);
      setState("saved");
    } catch {
      setState("error");
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="account-language" aria-labelledby="account-language-title">
      <h2 id="account-language-title">{props.t(titleKey)}</h2>
      <p>{props.t(copyKey)}</p>
      <div className="language-preference-options" role="group" aria-label={props.t(titleKey)}>
        {(["cs", "en"] as const).map((locale) => (
          <button
            className={`language-preference-button${props.locale === locale ? " active" : ""}`}
            type="button"
            aria-pressed={props.locale === locale}
            disabled={saving}
            onClick={() => void changeLocale(locale)}
            key={locale}
          >
            {props.t(locale === "cs" ? "common.languageCs" : "common.languageEn")}
          </button>
        ))}
      </div>
      {state === "saved" && <p className="message success-message" role="status">{props.t("account.language.saved")}</p>}
      {state === "error" && <p className="message error-message" role="alert">{props.t("account.language.error")}</p>}
    </section>
  );
}
